import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { CoreConfigService } from '@core/services/config.service';
import { Router } from '@angular/router';
import { IUser } from 'app/interfaces/user.interface';
import { ApiRoutes } from 'app/routes/app.routes';
import { AuthService } from 'app/services/auth/auth.service';
import { Cipher, LocalStore } from 'app/utils/localstore.utils';
import { LoaderService } from 'app/services/loader/loader.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RegisterComponent implements OnInit, OnDestroy {

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param auth
   * @param router
   * @param formBuilder
   * @param fileUpload
   * @param loaderService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private authService: AuthService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    public loaderService: LoaderService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }
  educationLevels: Record<string, any>[] = [
    {
      name: 'Primary',
    },
    {
      name: 'Secondary',
    },
    {
      name: 'Higher',
    },
  ];

  currentScreen = {
    user: {
      active: true,
      onEnter: false,
    },
    school: {
      active: false,
      onEnter: false,
    },
  };

  // Public
  public coreConfig: any;
  public passwordTextType: boolean;
  public registerForm: UntypedFormGroup;
  public submitted = false;

  // Private
  private _unsubscribeAll: Subject<any>;

  newUser = this.formBuilder.group({
    user: this.formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(3)]],
    }),
    school: this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      slug: ['', [Validators.required, Validators.minLength(2)]],
      contact_email: ['', [Validators.required, Validators.email]],
      type: ['', Validators.required],
    }),
  });

  ngOnInit(): void { }

  toggleScreens(screen: 'school' | 'user') {
    if (screen === 'user') {
      this.currentScreen.user = {
        active: false,
        onEnter: false,
      };
      this.currentScreen.school = {
        active: true,
        onEnter: true,
      };
    }
    if (screen === 'school') {
      this.currentScreen.user = {
        active: true,
        onEnter: true,
      };
      this.currentScreen.school = {
        active: false,
        onEnter: true,
      };
    }
  }

  /** **
   * @author Nsama David
   * @description method that returns AbstractControl for fom group belonging to particular group context
   * @param groupName string
   * @param controlName string
   * @returns AbstractControl AbstractControl
   */
  getFormControl(groupName: string, controlName: string): AbstractControl {
    return this.newUser.get(`${groupName}.${controlName}`);
  }

  createAccount() {
    if (this.newUser.invalid) { return; }

    const payload = this.newUser.value;
    payload.school.type = this.getFormControl('school', 'type').value?.name;

    this.authService.createAccount(payload)
      .subscribe((response) => {
        if (response?.status >= 200 && response?.status <= 300) {
          this.authActions(response);
        }
      });
  }

  authActions(response: any) {
    const responseData: IUser = response.data;
    LocalStore.setItem('token', responseData.refresh_token);
    LocalStore.setItem('x_api_key', responseData.x_api_key);
    LocalStore.setItem('user', responseData);
    // this.state.setUserState(responseData);
    this.router.navigate([`/app/${ApiRoutes.dashboard.home}`], {
      queryParams: {
        [responseData?.userType ?? 'admin']: Cipher.encrypt(responseData?.id),
      },
    });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
